// Server-side structured logging to PostHog Logs.
//
// The one entry point feature code uses. Route handlers, server actions, and
// Inngest functions call `logServer(level, event, attributes)`; nothing calls
// the OTel API directly. Sibling to `trackServer()` (product events) and
// `captureServerException()` (errors) — three narrow helpers over one vendor.
//
// WHAT TO USE THIS FOR
// Operational detail that helps a human debug an incident: a fallback fired, a
// retry was scheduled, an upstream returned an unexpected shape, a job
// finished. Not for product analytics (use `trackServer`) and not as a
// substitute for letting errors bubble to `onRequestError` (see
// `capture-exception.ts` — bias toward NOT catching).
//
// The two are complements, not alternatives: an exception gives you the stack,
// a log gives you the surrounding sequence. A defensive catch that already
// calls `captureServerException` usually does not also need a log line.
//
// STDOUT IS STILL WRITTEN. Every call also emits the same structured JSON to
// stdout. Three reasons: local dev has no PostHog project to read, Vercel logs
// stay usable as a fallback while ingestion is being verified (a missing
// PostHog project setting is indistinguishable from success from in here), and
// stdout is the only record that survives if the export request is cut.
//
// PII RULE — READ BEFORE ADDING A CALL SITE
// The `event` name is a static string from `MarkaLogEvent`; attributes are
// metadata only. Never interpolate user-typed text into either. Unlike the
// exception path, `before_send` does NOT run for logs — `scrubPiiText` here is
// the ONLY guardrail, and it is a coarse regex, not a filter you can rely on.
// The rule is upstream: don't pass PII.
//
// CONSENT: server logs are not consent-gated, consistent with
// `captureServerException` and the ungated cookieless tier described in
// `docs/observability.md`. Nothing is stored on the device; the mitigation for
// free-form text is the PII rule above.
//
// RUNTIME: Node only, same boundary as `getPostHogServer()` and the
// `onRequestError` hook. On edge the OTel provider is never registered, so the
// global logger is a no-op and only the stdout line is written.

import { logs, SeverityNumber } from "@opentelemetry/api-logs";
import { waitUntil } from "@vercel/functions";

import type { PosthogIds } from "./distinct-id";
import type { LogAttributes, LogAttributeValue, LogLevel, MarkaLogEvent } from "./log-events";
import { flushServerLogs, MARKA_SERVER_SERVICE_NAME } from "./otel-logs";
import { getReleaseProperties } from "./release";
import { scrubPiiText } from "./scrub";

export type { LogLevel } from "./log-events";

/**
 * What actually goes on the wire. `undefined` is dropped before this point
 * rather than represented, so OTel never sees a key with no value.
 */
type EmittedValue = string | number | boolean | null;

const SEVERITY: Record<LogLevel, { number: SeverityNumber; text: string }> = {
  debug: { number: SeverityNumber.DEBUG, text: "debug" },
  info: { number: SeverityNumber.INFO, text: "info" },
  warn: { number: SeverityNumber.WARN, text: "warn" },
  error: { number: SeverityNumber.ERROR, text: "error" },
};

/**
 * Which console method mirrors each level. `debug` deliberately maps to
 * `console.debug` rather than `console.log` so it can be filtered out of
 * Vercel logs separately.
 */
const CONSOLE_METHOD: Record<LogLevel, "debug" | "info" | "warn" | "error"> = {
  debug: "debug",
  info: "info",
  warn: "warn",
  error: "error",
};

export interface LogServerOptions {
  /**
   * Person + session context for a browser-originated request, from
   * `readPosthogIdsFromHeaders(request.headers)`.
   *
   * Stamped as `posthogDistinctId` / `sessionId` — the exact attribute keys
   * the browser SDK's `captureLog` emits, and the default value of PostHog's
   * `logs_distinct_id_attribute_keys`. Matching them is what makes a server
   * log filterable by person and jumpable to the session replay alongside the
   * client's own records.
   *
   * Omit for webhooks and Inngest jobs — there is no browser to attribute to,
   * and those identify by a stable customer-side ID passed as an attribute.
   */
  ids?: PosthogIds | null;
}

function scrubAttributeValue(value: LogAttributeValue): EmittedValue {
  return typeof value === "string" ? scrubPiiText(value) : (value ?? null);
}

/**
 * Emit one structured log record to PostHog Logs and stdout.
 *
 * Never throws: a logging failure must not take down the operation being
 * logged. Export failures surface through the OTel diag channel instead.
 */
export function logServer(
  level: LogLevel,
  event: MarkaLogEvent,
  attributes: LogAttributes = {},
  options: LogServerOptions = {}
): void {
  const severity = SEVERITY[level];
  const release = getReleaseProperties();

  const scrubbed: Record<string, EmittedValue> = { event };
  for (const [key, value] of Object.entries(attributes)) {
    if (value !== undefined) {
      scrubbed[key] = scrubAttributeValue(value);
    }
  }

  if (options.ids) {
    scrubbed.posthogDistinctId = options.ids.distinctId;
    if (options.ids.sessionId) {
      scrubbed.sessionId = options.ids.sessionId;
    }
  }
  if (release.$release_version) {
    scrubbed.$release_version = release.$release_version;
  }

  // stdout first, so a thrown/failed export can never cost us the record.
  // Shape matches the JSON the fulfillment modules already emit.
  console[CONSOLE_METHOD[level]](
    JSON.stringify({ level, ...scrubbed, service: MARKA_SERVER_SERVICE_NAME })
  );

  try {
    logs.getLogger(MARKA_SERVER_SERVICE_NAME).emit({
      severityNumber: severity.number,
      severityText: severity.text,
      // The body is the searchable free-text field. Keeping it equal to the
      // event name means `searchTerm` and the `event` attribute agree, and
      // there is no second place for text to leak in.
      body: event,
      attributes: scrubbed,
    });

    // `onEmit` starts the OTLP export but does NOT await it, and on Vercel
    // the lambda freezes the moment the response returns — which cuts the
    // in-flight export dead. Verified in production: records emitted right
    // before a fast 401 never arrived, while the identical pipeline works
    // from a long-lived local process. `waitUntil` keeps the function alive
    // until the flush settles (without delaying the response); `forceFlush`
    // drains every pending export on the provider, so this also rescues the
    // cold-start canary emitted outside any request scope on this instance.
    // (`@vercel/functions`, not next/server's `after`: Turbopack refuses
    // `after` in this module's import graph, and we don't want to defer the
    // flush anyway — just to survive the freeze.)
    waitUntil(flushServerLogs());
  } catch {
    // Swallowed on purpose — see "Never throws" above. `waitUntil` lands
    // here when there is no request context to attach to (local dev,
    // build-time prerendering): the process is long-lived there, so the
    // already-started export completes on its own. The stdout line is
    // written regardless, so nothing is lost but (at worst) the PostHog copy.
  }
}
